import { getHTMLFromFragment } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

// Same serializer as `editor.getHTML()`, so the measured length is exactly what gets saved.
const htmlLength = (doc: ProseMirrorNode) => getHTMLFromFragment(doc.content, doc.type.schema).length;

// Document position just after the first `retainedCharacters` code points of visible text.
function positionAfterCharacters(doc: ProseMirrorNode, retainedCharacters: number): number {
	let remainingCharacters = retainedCharacters;
	let cutPosition: number | null = null;

	doc.descendants((node, position) => {
		if (cutPosition !== null || !node.isText) return cutPosition === null;

		const characters = Array.from(node.text ?? '');
		if (characters.length < remainingCharacters) {
			remainingCharacters -= characters.length;
			return false;
		}

		cutPosition = position + characters.slice(0, remainingCharacters).join('').length;
		return false;
	});

	return cutPosition ?? doc.content.size;
}

export function trimEditorViewToHtmlLength(view: EditorView, maxLength: number): boolean {
	const { doc } = view.state;
	if (htmlLength(doc) <= maxLength) return false;

	const trimmedAt = (retainedCharacters: number) =>
		view.state.tr.delete(positionAfterCharacters(doc, retainedCharacters), doc.content.size);

	// Largest visible prefix whose serialized HTML still fits.
	let low = 0;
	let high = Array.from(doc.textContent).length;
	while (low < high) {
		const middle = Math.ceil((low + high) / 2);
		if (htmlLength(trimmedAt(middle).doc) <= maxLength) low = middle;
		else high = middle - 1;
	}

	view.dispatch(trimmedAt(low));
	return true;
}
